import React, { useState, useEffect, useRef } from 'react';
import {
  Layout, Row, Col, Card, Button, Input, Typography, Space, Tag, Table,
  Popconfirm, message, Spin, Divider, Statistic, Empty, Badge, Tooltip,
} from 'antd';
import {
  PlusOutlined, SendOutlined, DeleteOutlined, DownloadOutlined,
  CloudUploadOutlined, RobotOutlined, UserOutlined, FileExcelOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const API_BASE_URL = (() => {
  const { hostname, port, protocol } = window.location;
  if (!port || port === '80' || port === '443') return `${protocol}//${hostname}/api/v1`;
  if (port === '3000') return `http://${hostname}:8000/api/v1`;
  return `http://${hostname}:${port}/api/v1`;
})();

const token = () => localStorage.getItem('spo_auth_token');

const api = {
  list: () => fetch(`${API_BASE_URL}/quotation/`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  create: () => fetch(`${API_BASE_URL}/quotation/`, { method: 'POST', headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).then(r => r.json()),
  get: (id) => fetch(`${API_BASE_URL}/quotation/${id}`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  delete: (id) => fetch(`${API_BASE_URL}/quotation/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  chat: (id, msg) => fetch(`${API_BASE_URL}/quotation/${id}/chat`, { method: 'POST', headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ message: msg }) }).then(r => r.json()),
  confirm: (id) => fetch(`${API_BASE_URL}/quotation/${id}/confirm`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  export: (id) => fetch(`${API_BASE_URL}/quotation/${id}/export`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  downloadUrl: (id) => `${API_BASE_URL}/quotation/${id}/download?token=${token()}`,
  uploadDrive: (id) => fetch(`${API_BASE_URL}/quotation/${id}/upload-drive`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
  clone: (id) => fetch(`${API_BASE_URL}/quotation/${id}/clone`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
};

const STATUS_COLOR = { draft: 'blue', confirmed: 'green', exported: 'cyan', uploaded: 'purple' };
const STATUS_LABEL = { draft: '草稿', confirmed: '已確認', exported: '已匯出', uploaded: '已上傳' };

const fmt = (n) => n != null ? `NT$${Number(n).toLocaleString()}` : '—';

// ── Quote Preview Panel ───────────────────────────────────────────────────────

function QuotePreview({ quoteData, quotationId, status, gdrive_url, gdrive_folder_url, onExport, onUpload, exporting }) {
  if (!quoteData) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
        <div style={{ textAlign: 'center' }}>
          <RobotOutlined style={{ fontSize: 48, marginBottom: 12 }} />
          <div>AI 正在收集報價資訊...</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>完成對話後報價單將自動出現在此</div>
        </div>
      </div>
    );
  }

  const { client = {}, summary = {}, items = [], quote_number, quote_date, valid_days, payment_terms, notes } = quoteData;
  const sections = ['A', 'B', 'C', 'D'];

  return (
    <div style={{ overflowY: 'auto', height: '100%', paddingRight: 4 }}>
      {/* Header */}
      <Card
        size="small"
        style={{ marginBottom: 12, background: 'linear-gradient(135deg, #1a1a2e, #003a8c)', border: 'none' }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div style={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>Comboware marcom AI Studio</div>
        <div style={{ color: '#a5b4fc', fontSize: 11, marginTop: 2 }}>AI 品牌能見度 — 年度內容與監測服務提案</div>
        <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {[
            ['報價編號', quote_number],
            ['日期', quote_date],
            ['公司', client.company],
            ['產業', client.industry],
            ['市場', (client.markets || []).join('+')],
          ].map(([k, v]) => v ? (
            <div key={k}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{k} </span>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{v}</span>
            </div>
          ) : null)}
        </div>
      </Card>

      {/* Items by section */}
      {sections.map(sec => {
        const secItems = items.filter(i => i.section === sec && i.included);
        if (!secItems.length) return null;
        const secColors = { A: '#4472C4', B: '#70AD47', C: '#ED7D31', D: '#7030A0' };
        return (
          <div key={sec} style={{ marginBottom: 12 }}>
            <div style={{
              background: secColors[sec], color: '#fff', fontWeight: 700, fontSize: 11,
              padding: '4px 10px', borderRadius: '4px 4px 0 0',
            }}>
              {sec === 'A' ? '【A】一次性建置費用' : sec === 'B' ? '【B】年度訂閱服務' : sec === 'C' ? '【C】內容製作服務' : '【D】選購加值服務'}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['服務項目', '單價', '數量', '小計'].map(h => (
                    <th key={h} style={{ padding: '4px 6px', textAlign: h === '服務項目' ? 'left' : 'right', fontWeight: 600, borderBottom: '1px solid #e8e8e8' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {secItems.map((item, i) => (
                  <tr key={item.code} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>{item.name}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>{fmt(item.unit_after_disc)}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{item.qty}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(item.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Summary */}
      <Card size="small" style={{ marginBottom: 12, background: '#f9f9f9' }}>
        {[
          ['A+B+C 稅前合計', summary.subtotal_abc],
          summary.discount_amount ? ['折扣', -summary.discount_amount] : null,
          summary.discount_amount ? ['優惠後合計', summary.subtotal_after_discount] : null,
          ['營業稅 5%', summary.tax_amount],
        ].filter(Boolean).map(([label, val]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
            <span style={{ color: '#666' }}>{label}</span>
            <span style={{ color: val < 0 ? '#cf1322' : '#333', fontWeight: 500 }}>{fmt(val)}</span>
          </div>
        ))}
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>✦ 正式服務含稅總計</span>
          <span style={{ fontWeight: 900, fontSize: 16, color: '#C00000' }}>{fmt(summary.grand_total_abc)}</span>
        </div>
        {summary.section_d_total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px dashed #e0c3fc', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: '#7030A0' }}>+ D 選購（含稅）</span>
            <span style={{ fontSize: 12, color: '#7030A0', fontWeight: 600 }}>{fmt(summary.section_d_total + summary.section_d_tax)}</span>
          </div>
        )}
      </Card>

      {/* Payment terms */}
      {payment_terms && (
        <Card size="small" style={{ marginBottom: 12, background: '#f0f4ff', borderColor: '#adc6ff' }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: '#2d3748', marginBottom: 4 }}>付款方式</div>
          <div style={{ fontSize: 12, color: '#333', whiteSpace: 'pre-wrap' }}>{payment_terms}</div>
        </Card>
      )}
      {notes && (
        <Card size="small" style={{ marginBottom: 12, background: '#fffbe6', borderColor: '#ffe58f' }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: '#874d00', marginBottom: 4 }}>備註</div>
          <div style={{ fontSize: 12, color: '#333', whiteSpace: 'pre-wrap' }}>{notes}</div>
        </Card>
      )}

      {/* Actions */}
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button
          type="primary" icon={<FileExcelOutlined />} block
          loading={exporting} onClick={onExport}
          disabled={status === 'draft'}
        >
          {status === 'uploaded' || status === 'exported' ? '重新生成 Excel & 上傳' : '生成 Excel 報價單'}
        </Button>
        {quoteData && quotationId && (
          <Button
            icon={<DownloadOutlined />} block
            onClick={() => window.open(api.downloadUrl(quotationId), '_blank')}
            disabled={status === 'draft' || status === 'confirmed'}
          >
            下載 Excel
          </Button>
        )}
        {gdrive_url && (
          <Button
            icon={<CloudUploadOutlined />} block
            onClick={() => window.open(gdrive_url, '_blank')}
            style={{ borderColor: '#722ed1', color: '#722ed1' }}
          >
            在 Drive 開啟檔案 ↗
          </Button>
        )}
        {!gdrive_url && (
          <Button
            icon={<CloudUploadOutlined />} block onClick={onUpload}
            disabled={status !== 'exported' && status !== 'uploaded'}
          >
            上傳 Google Drive
          </Button>
        )}
      </Space>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function QuotationPage() {
  const [quotations, setQuotations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const loadList = async () => {
    try {
      const data = await api.list();
      setQuotations(Array.isArray(data) ? data : []);
    } catch (e) { message.error('載入報價列表失敗'); }
  };

  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectQuotation = async (id) => {
    setLoading(true);
    try {
      const data = await api.get(id);
      setSelected(data);
      setMessages(data.messages || []);
    } catch (e) { message.error('載入報價失敗'); }
    setLoading(false);
  };

  const createNew = async () => {
    setCreating(true);
    try {
      const data = await api.create();
      setQuotations(prev => [data, ...prev]);
      setSelected(data);
      setMessages(data.messages || []);
    } catch (e) { message.error('建立失敗'); }
    setCreating(false);
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    try {
      const res = await api.confirm(selected.id);
      if (res.reply) setMessages(prev => [...prev, { role: 'assistant', content: res.reply, id: Date.now() }]);
      setSelected(prev => ({ ...prev, quote_data: res.quote_data, status: res.status }));
      if (res.quote_data) {
        message.success('報價單已生成！右側預覽已更新。');
        setQuotations(prev => prev.map(q => q.id === selected.id
          ? { ...q, status: res.status, total_amount: res.quote_data?.summary?.grand_total_abc } : q));
      } else {
        message.warning('AI 未能產生報價，請繼續補充資訊後再試。');
      }
    } catch (e) { message.error('確認失敗'); }
    setConfirming(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || !selected) return;
    const msg = input.trim();
    setInput('');
    setSending(true);

    setMessages(prev => [...prev, { role: 'user', content: msg, id: Date.now() }]);
    try {
      const res = await api.chat(selected.id, msg);
      setMessages(prev => [...prev, { role: 'assistant', content: res.reply, id: Date.now() + 1 }]);
      setSelected(prev => ({ ...prev, quote_data: res.quote_data, status: res.status }));
      if (res.quote_data) {
        setQuotations(prev => prev.map(q => q.id === selected.id
          ? { ...q, status: res.status, total_amount: res.quote_data?.summary?.grand_total_abc }
          : q));
      }
    } catch (e) { message.error('發送失敗'); }
    setSending(false);
  };

  const handleExport = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const res = await api.export(selected.id);
      if (res.ok) {
        const newStatus = res.gdrive_url ? 'uploaded' : 'exported';
        setSelected(prev => ({
          ...prev,
          status: newStatus,
          gdrive_url: res.gdrive_url || prev.gdrive_url,
          gdrive_folder_url: res.gdrive_folder_url || prev.gdrive_folder_url,
          excel_exported: true,
        }));
        loadList();
        if (res.gdrive_url) {
          message.success({
            content: (
              <span>
                {res.message} —{' '}
                <a href={res.gdrive_url} target="_blank" rel="noreferrer">開啟檔案</a>
                {res.gdrive_folder_url && (
                  <> | <a href={res.gdrive_folder_url} target="_blank" rel="noreferrer">開啟資料夾</a></>
                )}
              </span>
            ),
            duration: 8,
          });
        } else {
          message.warning(res.message, 6);
        }
      } else {
        message.error(res.message || res.detail || '匯出失敗');
      }
    } catch (e) { message.error('匯出失敗'); }
    setExporting(false);
  };

  const handleUpload = async () => {
    if (!selected) return;
    try {
      const res = await api.uploadDrive(selected.id);
      if (res.ok) { message.success('已上傳至 Google Drive'); setSelected(prev => ({ ...prev, gdrive_url: res.url, status: 'uploaded' })); }
      else message.error(res.error || '上傳失敗');
    } catch (e) { message.error('上傳失敗'); }
  };

  const handleClone = async (id) => {
    try {
      const data = await api.clone(id);
      setQuotations(prev => [data, ...prev]);
      setSelected(data);
      setMessages(data.messages || []);
      message.success('已複製報價，AI 已載入原始報價內容作為對話背景');
    } catch (e) { message.error('複製失敗'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(id);
      setQuotations(prev => prev.filter(q => q.id !== id));
      if (selected?.id === id) { setSelected(null); setMessages([]); }
      message.success('已刪除');
    } catch (e) { message.error('刪除失敗'); }
  };

  const listColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '客戶', dataIndex: 'client_company', ellipsis: true, render: (v, r) => v || r.title || `報價 #${r.id}` },
    { title: '狀態', dataIndex: 'status', width: 80, render: s => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag> },
    { title: '金額', dataIndex: 'total_amount', width: 110, render: v => v ? fmt(v) : '—', align: 'right' },
    {
      title: '', width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => selectQuotation(r.id)}>開啟</Button>
          <Tooltip title="複製此報價作為新對話起點（AI 將完整知道此報價內容）">
            <Button size="small" type="link" onClick={() => handleClone(r.id)}>複製</Button>
          </Tooltip>
          <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          <FileExcelOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          AI 報價產生器
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadList}>重新整理</Button>
          <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={createNew}>
            新報價
          </Button>
        </Space>
      </div>

      {/* Past quotes list */}
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '8px 0' }}>
        <Table
          dataSource={quotations}
          columns={listColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 5, size: 'small' }}
          rowClassName={r => r.id === selected?.id ? 'ant-table-row-selected' : ''}
          style={{ cursor: 'pointer' }}
        />
      </Card>

      {/* Main split panel */}
      {selected ? (
        <Row gutter={16} style={{ flex: 1, overflow: 'hidden' }}>
          {/* Left: Chat */}
          <Col span={12} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card
              size="small"
              title={
                <Space>
                  <RobotOutlined style={{ color: '#1890ff' }} />
                  <span>報價對話</span>
                  <Tag color={STATUS_COLOR[selected.status]}>{STATUS_LABEL[selected.status]}</Tag>
                  {selected.gdrive_url && (
                    <a href={selected.gdrive_url} target="_blank" rel="noreferrer">
                      <Tag color="purple">Drive 檔案 ↗</Tag>
                    </a>
                  )}
                  {selected.gdrive_folder_url && (
                    <a href={selected.gdrive_folder_url} target="_blank" rel="noreferrer">
                      <Tag color="geekblue">Drive 資料夾 ↗</Tag>
                    </a>
                  )}
                </Space>
              }
              style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 12 }}
            >
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', marginBottom: 12, paddingRight: 4 }}>
                {loading ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div> : messages.map((m, i) => (
                  <div key={m.id || i} style={{
                    display: 'flex', flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start', gap: 8, marginBottom: 12,
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: m.role === 'user' ? '#1890ff' : '#52c41a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14,
                    }}>
                      {m.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    </div>
                    <div style={{
                      maxWidth: '75%', padding: '8px 12px', borderRadius: 12,
                      background: m.role === 'user' ? '#e6f7ff' : '#f6ffed',
                      border: `1px solid ${m.role === 'user' ? '#91d5ff' : '#b7eb8f'}`,
                      fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                    }}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#52c41a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                      <RobotOutlined />
                    </div>
                    <Spin size="small" />
                    <Text type="secondary" style={{ fontSize: 12 }}>AI 正在思考報價...</Text>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div style={{ display: 'flex', gap: 8 }}>
                <TextArea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="描述客戶需求，或回覆 AI 的問題... (Enter 送出，Shift+Enter 換行)"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={sending || confirming}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Button
                    type="primary" icon={<SendOutlined />} onClick={sendMessage}
                    loading={sending} disabled={confirming} style={{ flex: 1 }}
                  >
                    送出
                  </Button>
                  <Button
                    icon={<FileExcelOutlined />} onClick={handleConfirm}
                    loading={confirming} disabled={sending}
                    style={{ flex: 1, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontSize: 11 }}
                  >
                    確認生成報價
                  </Button>
                </div>
              </div>
            </Card>
          </Col>

          {/* Right: Quote Preview */}
          <Col span={12} style={{ height: '100%' }}>
            <Card
              size="small"
              title={<Space><FileExcelOutlined style={{ color: '#52c41a' }} /><span>報價預覽</span></Space>}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              bodyStyle={{ flex: 1, overflow: 'hidden', padding: 12, display: 'flex', flexDirection: 'column' }}
            >
              <QuotePreview
                quoteData={selected.quote_data}
                quotationId={selected.id}
                status={selected.status}
                gdrive_url={selected.gdrive_url}
                gdrive_folder_url={selected.gdrive_folder_url}
                onExport={handleExport}
                onUpload={handleUpload}
                exporting={exporting}
              />
            </Card>
          </Col>
        </Row>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Empty
            description="請從上方列表選擇報價，或點擊「新報價」開始"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={createNew} loading={creating}>
              新增報價
            </Button>
          </Empty>
        </div>
      )}
    </div>
  );
}
